import { type FC } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

/**
 * AdminOrg page for viewing organization details.
 * Uses Shadcn UI Card and Tailwind for layout.
 */
export const AdminOrgDetail: FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { data: organization, isLoading, error } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const response = await fetch(`/api/org/${orgId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch organization details');
      }
      return response.json();
    },
    enabled: !!orgId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load organization. Please try again.
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-yellow-50 p-4 rounded-md text-yellow-800">
          Organization not found.
        </div>
      </div>
    );
  }

  // Destructure with fallback for missing fields
  const { name, description, createdAt } = organization;

  return (
    <div className="flex justify-center items-start min-h-screen py-8">
      <Card className="w-full max-w-xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{name || "Unnamed Organization"}</CardTitle>
          <a
            href={`/app/admin/org/${orgId}/edit`}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-white hover:bg-primary/90 transition"
          >
            Edit
          </a>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <span className="block text-muted-foreground text-xs mb-1">Org ID:</span>
            <span className="font-mono text-sm">{orgId}</span>
          </div>
          <div className="mb-4">
            <span className="block text-muted-foreground text-xs mb-1">Description:</span>
            <span>{description || <span className="italic text-gray-400">No description</span>}</span>
          </div>
          <div className="mb-4">
            <span className="block text-muted-foreground text-xs mb-1">Created:</span>
            <span>{createdAt ? new Date(createdAt).toLocaleDateString() : <span className="italic text-gray-400">Unknown</span>}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
